import {User} from '../../entity/model/user';
import {UserPermission} from '../../entity/enums/user-permission';
import UserStore from '../store/user-store';
import {ManageMode} from '../../entity/enums/manage-mode';
import {UserDto} from '../../entity/dto/user-dto';
import {RequestBody} from '../../entity/core/request-body';
import {toast} from 'react-toastify';
import {UserRole} from '../../entity/enums/user-role';
import {SearchInputDto} from '../../entity/dto/search-input-dto';
import {UserOnboardDto, UserDetailsDto, OrganizationDetailsDto} from '../../entity/dto/user-onboard-dto';
import {UserInvitationDto} from '../../entity/dto/user-invitation-dto';
import {OrganizationType} from '../../entity/enums/organization-type';

export default class UserService {
  userStore: UserStore;

  constructor(userStore: UserStore) {
    this.userStore = userStore;
  }

  getCurrentUser = async (): Promise<User | undefined> => {
    return this.userStore.currentUser ?? await this.userStore.getCurrentUser();
  }

  getUserById = async(userId: string): Promise<User | undefined> => {
    return await this.userStore.getUserById(userId);
  }

  getAllUsers = async (organizationId: string, reload: boolean): Promise<User[]> => {
    return this.userStore.cache && this.userStore.users.filter((user: User) => user.organizationID === organizationId).length > 0 && !reload
      ? this.userStore.users.filter((user: User) => user.isActive)
      : (await this.userStore.getAllUsers(organizationId)).filter((user: User) => user.isActive);
  }

  createUser = async (organizationID: string, userPayload: UserDto): Promise<void> => {
    const requestBody: RequestBody = { body: userPayload };
    const response = await this.userStore.createUser(organizationID, requestBody);

    if (!response)
      toast.success('User created successfully !!');
    else
      toast.error(response);
  }

  updateUser = async (userId: string, userPayload: UserDto): Promise<void> => {
    const requestBody: RequestBody = {body: userPayload};
    const response = await this.userStore.updateUser(userPayload.organizationID, userId, requestBody);

    if (!response)
      toast.success('User updated successfully !!');
    else
      toast.error(response);
  }

  deleteUser = async (organizationId: string, userId: string): Promise<void> => {
    const response = await this.userStore.deleteUser(organizationId, userId);

    if (!response)
      toast.success('User deleted successfully !!');
    else
      toast.error(response);
  }

  detachUser = async (organizationId: string, userId: string): Promise<void> => {
    const response = await this.userStore.detachUser(organizationId, userId);

    if (!response)
      toast.success('User detached successfully !!');
    else
      toast.error(response);
  }

  logout = async (): Promise<void> => {
    const response = await this.userStore.logout();
    if (response)
      toast.error(response);
  }

  isLoggedIn = (): boolean => {
    return this.userStore.isLoggedIn;
  }

  setLoggedIn = (status: boolean): void => {
    this.userStore.isLoggedIn = status;
  }

  isPermitted = (permission: UserPermission): boolean => {
    const currentUser = this.userStore.currentUser;
    return currentUser ? currentUser.permissions.includes(permission.toString()) : false;
  }

  isAdminUser = (): boolean => {
    const currentUser = this.userStore.currentUser;
    return currentUser ? currentUser.roles.filter((role: string): boolean => role === UserRole.ADMIN || role === UserRole.ORG_ADMIN).length > 0 : false;
  }

  isSuperAdminUser = (): boolean => {
    const currentUser = this.userStore.currentUser;
    return currentUser ? currentUser.roles.filter((role: string): boolean => role === UserRole.ADMIN).length > 0 : false;
  }

  getDisplayName = (isCurrentUser: boolean, userId: string): string => {
    const currentUser = this.userStore.currentUser;
    const user = this.userStore.users.find((user: User) => user.id === userId);

    if (isCurrentUser)
      return currentUser ? currentUser.firstName + ' ' + currentUser.lastName : '';

    if (user)
      return user.firstName + ' ' + user.lastName;

    return '';
  }

  getCache = (): boolean => {
    return this.userStore.cache;
  }

  filterUsers = (input: string): User[] => {
    return this.userStore.users.filter((user: User) => {
      if (!user.isActive) return false;
      
      const matchesFirstName = user.firstName.trim().toLowerCase().startsWith(input);
      const matchesLastName = user.lastName.trim().toLowerCase().startsWith(input);
      const matchesEmail = user.email.trim().toLowerCase().startsWith(input);
      return matchesEmail || matchesFirstName || matchesLastName;
    });
  }

  getUserDto = (mode: ManageMode, organizationId: string, user?: User): UserDto => {
    return {
      organizationID: organizationId,
      firstName: mode === ManageMode.CREATE ? '' : user?.firstName ?? '',
      lastName: mode === ManageMode.CREATE ? '' : user?.lastName ?? '',
      email: mode === ManageMode.CREATE ? '' : user?.email ?? '',
      roles: mode === ManageMode.CREATE ? '' : user?.roles[0] ?? '',
      isPrimaryContact: mode === ManageMode.CREATE ? false : user?.isPrimaryContact ?? false
    }
  }

  getSearchDto = (): SearchInputDto => {
    return {
      searchInput: ''
    };
  }

  getUserDetailsDto = (userOnboardDto?: UserOnboardDto): UserDetailsDto => {
    const currentUser = this.userStore.currentUser;

    if (userOnboardDto?.firstName || userOnboardDto?.lastName) {
      return {
        firstName: userOnboardDto.firstName || '',
        lastName: userOnboardDto.lastName || ''
      };
    }

    if (currentUser?.email && currentUser?.firstName && currentUser?.lastName) {
      const emailPrefix = currentUser.email.split('@')[0];
      const isDefaultFirstName = currentUser.firstName === emailPrefix;
      const isDefaultLastName = currentUser.lastName === 'Guest';

      if (isDefaultFirstName && isDefaultLastName) {
        return {
          firstName: '',
          lastName: ''
        };
      }
    }

    return {
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || ''
    };
  }

  getOrganizationDetailsDto = (userOnboardDto?: UserOnboardDto): OrganizationDetailsDto => {
    const organization = userOnboardDto?.organization;
    return {
      name: organization?.name || '',
      type: organization?.type || OrganizationType.ABSTRACTOR,
      address: {
        line1: organization?.address?.line1 || '',
        city: organization?.address?.city || '',
        state: organization?.address?.state || '',
        zip: organization?.address?.zip || ''
      },
      phoneNumber: organization?.phoneNumber || ''
    };
  }

  onboardUser = async (userId: string, userOnboardDto: UserOnboardDto): Promise<boolean> => {
    const requestBody: RequestBody = { body: userOnboardDto };
    const response = await this.userStore.onboardUser(userId, requestBody);

    if (!response) {
      toast.success('User onboarded successfully !!');
      return true;
    } else {
      toast.error(response);
      return false;
    }
  }

  sendInvitation = async (organizationID: string, invitationPayload: UserInvitationDto): Promise<void> => {
    const requestBody: RequestBody = { body: invitationPayload };
    const response = await this.userStore.sendInvitation(organizationID, requestBody);

    if (!response)
      toast.success('User invitation sent successfully !!');
    else
      toast.error(response);
  }

  getUserInvitations = async (organizationId: string, reload: boolean = false): Promise<UserInvitationDto[]> => {
    return this.userStore.cache && this.userStore.userInvitations.length > 0 && !reload
      ? this.userStore.userInvitations
      : await this.userStore.getUserInvitations(organizationId);
  }

  filterUserInvitations = (searchInput: string): UserInvitationDto[] => {
    if (!searchInput.trim())
      return this.userStore.userInvitations;

    const input = searchInput.toLowerCase();
    return this.userStore.userInvitations.filter(invitation => {
      const rolesString = Array.isArray(invitation.roles) ? invitation.roles.join(', ') : invitation.roles;
      return invitation.email.toLowerCase().includes(input) ||
        rolesString.toLowerCase().includes(input) ||
        invitation.status?.toLowerCase().includes(input);
    });
  }

  resendInvitation = async (organizationId: string, invitationId: string): Promise<void> => {
    const response = await this.userStore.resendInvitation(organizationId, invitationId);

    if (!response)
      toast.success('Invitation resent successfully !!');
    else
      toast.error(response);
  }

  deleteInvitation = async (organizationId: string, invitationId: string): Promise<void> => {
    const response = await this.userStore.deleteInvitation(organizationId, invitationId);

    if (!response)
      toast.success('Invitation deleted successfully !!');
    else
      toast.error(response);
  }

  acceptInvitation = async (invitationCode: string, firstName: string, lastName: string): Promise<boolean> => {
    const response = await this.userStore.acceptInvitation(invitationCode, firstName, lastName);
    
    if (!response) {
      toast.success('Invitation accepted successfully! Welcome to the organization.');
      return true;
    } else {
      toast.error(response);
      return false;
    }
  }
}
